import { NgModule, LOCALE_ID, isDevMode } from '@angular/core';
import { DATE_PIPE_DEFAULT_TIMEZONE, registerLocaleData } from '@angular/common';
import localeEnIn from '@angular/common/locales/en-IN';
import { BrowserModule } from '@angular/platform-browser';

registerLocaleData(localeEnIn);

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './authentication/login/login/login.component';
import { NgOptimizedImage } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DashboardComponent } from './dashboard/dashboard/dashboard.component';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { authInterceptor } from './authentication/auth.interceptor';
import { SidebarComponent } from './common/components/sidebar/sidebar.component';
import { LineChartComponent } from './common/components/line-chart/line-chart.component';
import { EmployeesComponent } from './employees/components/employees/employees.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EmployeeInfoFormComponent } from './employees/components/employee-info-form/employee-info-form.component';
import { DealersComponent } from './dealers/components/dealers/dealers.component';
import { DealerDetailsComponent } from './dealers/components/dealer-details/dealer-details.component';
import { DealersInfoFormComponent } from './dealers/components/dealers-info-form/dealers-info-form.component';
import { EmployeeDetailsComponent } from './employees/components/employee-details/employee-details.component';
import { DeleteConfirmationModalComponent } from './common/components/delete-confirmation-modal/delete-confirmation-modal.component';
import { ConfirmationModalComponent } from './common/components/confirmation-modal/confirmation-modal.component';
import { PageNotFoundComponent } from './common/components/page-not-found/page-not-found.component';
import { ExpensesComponent } from './expenses/components/expenses/expenses.component';
import { ExpenseSummaryCardComponent } from './expenses/components/expense-summary-card/expense-summary-card.component';
import { EmployeeTravelReportsComponent } from './expenses/components/employee-travel-reports/employee-travel-reports.component';
import { EmployeeTravelLogComponent } from './expenses/components/employee-travel-log/employee-travel-log.component';
import { AttendanceComponent } from './attendance/components/attendance/attendance.component';
import { AttendanceReportModalComponent } from './attendance/components/attendance-report-modal/attendance-report-modal.component';
import { AttendanceDetailComponent } from './attendance/components/attendance-detail/attendance-detail.component';
import { ProductsComponent } from './products/components/products/products.component';
import { PaginationComponent } from './common/components/pagination/pagination.component';
import { SettingsComponent } from './settings/components/settings/settings.component';
import { RoleManagementComponent } from './settings/components/role-management/role-management.component';
import { FindActionPipe } from './common/pipes/find-action.pipe';

import { ResetPasswordModalComponent } from './common/components/reset-password-modal/reset-password-modal.component';
import { AttendanceStatusCardComponent } from './attendance/components/attendance-status-card/attendance-status-card.component';
import { AttendanceHistoryComponent } from './attendance/components/attendance-history/attendance-history.component';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { PurchasedOrderComponent } from './purchased-order/components/purchased-order/purchased-order.component';
import { SuppliersComponent } from './purchased-order/components/suppliers/suppliers.component';
import { SupplierInfoFormComponent } from './purchased-order/components/supplier-info-form/supplier-info-form.component';
import { TableComponent } from './common/components/table/table.component';
import { PurchaseOrderFormComponent } from './purchased-order/components/purchase-order-form/purchase-order-form.component';
import { RawMaterialOverviewComponent } from './raw-material-inventory/components/raw-material-overview/raw-material-overview.component';
import { RawMaterialCategoriesComponent } from './app/raw-material-inventory/components/raw-material-categories/raw-material-categories.component';
import { RawMaterialComponent } from './raw-material-inventory/components/raw-material/raw-material.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ProductionOverviewComponent } from './production-inventory/components/production-overview/production-overview.component';
import { InventoryOverviewComponent } from './production-inventory/components/inventory-overview/inventory-overview.component';
import { WarehousesComponent } from './production-inventory/components/warehouses/warehouses.component';
import { LaboursComponent } from './production-inventory/components/labours/labours.component';
import { LaboursDailyAttendanceComponent } from './production-inventory/components/labours-daily-attendance/labours-daily-attendance.component';
import { GoogleMapsModule } from '@angular/google-maps';
import { InventoryComponent } from './raw-material-inventory/components/inventory/inventory.component';
import { PartyVisitsComponent } from './party-visits/components/party-visits/party-visits.component';
import { EmployeeVisitDetailsComponent } from './party-visits/components/employee-visit-details/employee-visit-details.component';
import { RawMaterialEntryComponent } from './raw-material-inventory/components/raw-material-entry/raw-material-entry.component';
import { FarmersComponent } from './farmers/component/farmers/farmers.component';
import { FarmerDetailsComponent } from './farmers/component/farmer-details/farmer-details.component';
import { EmployeeFarmersComponent } from './farmers/component/employee-farmers/employee-farmers.component';
import { MultiSelectDropdownComponent } from './common/multi-select-dropdown/multi-select-dropdown.component';
import { InfoAlertComponent } from './common/components/info-alert/info-alert.component';
import { ProductDetailsComponent } from './products/components/product-details/product-details.component';
import { ProductStockListComponent } from './production-inventory/components/product-stock-list/product-stock-list.component';
import { ProductStockDetailComponent } from './production-inventory/components/product-stock-detail/product-stock-detail.component';
import { ProductionEntryFormComponent } from './production-inventory/components/production-entry-form/production-entry-form.component';
import { ProductionEntriesComponent } from './production-inventory/components/production-entries/production-entries.component';
import { ProductStockAdjustmentFormComponent } from './production-inventory/components/product-stock-adjustment-form/product-stock-adjustment-form.component';
import { ProductStockAdjustmentsComponent } from './production-inventory/components/product-stock-adjustments/product-stock-adjustments.component';
import { ProductPackagingDetailsComponent } from './products/components/product-packaging-details/product-packaging-details.component';
import { ErrorAlertComponent } from './common/components/error-alert/error-alert.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { OrdersOverviewComponent } from './orders/components/orders-overview/orders-overview.component';
import { PageWrapperComponent } from './common/components/page-wrapper/page-wrapper.component';
import { TabNavigationComponent } from './common/components/tab-navigation/tab-navigation.component';
import { TransportersComponent } from './orders/components/transporters/transporters.component';
import { PrimaryBtnComponent } from './common/components/primary-btn/primary-btn.component';
import { LoadingSpinnerComponent } from './common/components/loading-spinner/loading-spinner.component';
import { OrdersListComponent } from './orders/components/orders-list/orders-list.component';
import { PaymentsComponent } from './payments/components/payments/payments.component';

import { PaymentDetailsComponent } from './payments/components/payment-details/payment-details.component';
import { ShipmentsComponent } from './orders/components/shipments/shipments.component';
import { PendingOrdersComponent } from './orders/components/pending-orders/pending-orders.component';
import { OrderEntityFiltersComponent } from './orders/components/order-entity-filters/order-entity-filters.component';
import { LabourAdvanceLedgerComponent } from './production-inventory/components/labour-advance-ledger/labour-advance-ledger.component';
import { AdvanceBalanceComponent } from './production-inventory/components/advance-balance/advance-balance.component';
import { OrderDetailsComponent } from './orders/components/order-details/order-details.component';
import { ShipmentDetailsComponent } from './orders/components/shipment-details/shipment-details.component';
import { IsAdminUserDirective } from './common/directives/is-admin-user.directive';
import { NotAuthorizedComponent } from './common/components/not-authorized/not-authorized.component';
import { StockAdjustmentsComponent } from './raw-material-inventory/components/stock-adjustments/stock-adjustments.component';
import { StockAdjustmentFormComponent } from './raw-material-inventory/components/stock-adjustment-form/stock-adjustment-form.component';
import { CompaniesComponent } from './production-inventory/components/companies/companies.component';
import { FactoriesComponent } from './production-inventory/components/factories/factories.component';
import { FactoryStockComponent } from './production-inventory/components/factory-stock/factory-stock.component';
import { ProductTransferComponent } from './production-inventory/components/product-transfer/product-transfer.component';
import { ProductionStockComponent } from './production-inventory/components/production-stock/production-stock.component';
import { MasterComponent } from './master/master.component';
import { RolesComponent } from './employees/components/roles/roles.component';
import { DesignationsComponent } from './employees/components/designations/designations.component';
import { SalesTeamManagementComponent } from './features/sales-team-management/pages/sales-team-management/sales-team-management.component';
import { SalesTeamRowComponent } from './features/sales-team-management/components/sales-team-row/sales-team-row.component';
import { ManagerPickerModalComponent } from './features/sales-team-management/components/manager-picker-modal/manager-picker-modal.component';
import { RemoveManagerModalComponent } from './features/sales-team-management/components/remove-manager-modal/remove-manager-modal.component';

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    DashboardComponent,
    SidebarComponent,
    LineChartComponent,
    EmployeesComponent,
    EmployeeInfoFormComponent,
    DealersComponent,
    DealerDetailsComponent,
    DealersInfoFormComponent,
    EmployeeDetailsComponent,
    DeleteConfirmationModalComponent,
    ConfirmationModalComponent,
    PageNotFoundComponent,
    ExpensesComponent,
    EmployeeTravelReportsComponent,
    EmployeeTravelLogComponent,
    AttendanceComponent,
    ProductsComponent,
    PaginationComponent,
    SettingsComponent,
    RoleManagementComponent,
    FindActionPipe,

    ResetPasswordModalComponent,
    AttendanceStatusCardComponent,
    AttendanceHistoryComponent,
    PurchasedOrderComponent,
    SuppliersComponent,
    SupplierInfoFormComponent,
    TableComponent,
    PurchaseOrderFormComponent,
    RawMaterialOverviewComponent,
    RawMaterialCategoriesComponent,
    RawMaterialComponent,
    ProductionOverviewComponent,
    InventoryOverviewComponent,
    WarehousesComponent,
    LaboursComponent,
    LaboursDailyAttendanceComponent,
    InventoryComponent,
    PartyVisitsComponent,
    EmployeeVisitDetailsComponent,
    RawMaterialEntryComponent,
    FarmersComponent,
    FarmerDetailsComponent,
    EmployeeFarmersComponent,
    MultiSelectDropdownComponent,
    InfoAlertComponent,
    ProductDetailsComponent,
    ProductStockListComponent,
    ProductStockDetailComponent,
    ProductionEntryFormComponent,
    ProductionEntriesComponent,
    ProductStockAdjustmentFormComponent,
    ProductStockAdjustmentsComponent,
    ProductPackagingDetailsComponent,
    ErrorAlertComponent,
    OrdersOverviewComponent,
    TransportersComponent,
    OrdersListComponent,
    PaymentsComponent,
    PaymentDetailsComponent,
    ShipmentsComponent,
    PendingOrdersComponent,
    OrderEntityFiltersComponent,
    LabourAdvanceLedgerComponent,
    AdvanceBalanceComponent,
    OrderDetailsComponent,
    IsAdminUserDirective,
    NotAuthorizedComponent,
    StockAdjustmentsComponent,
    StockAdjustmentFormComponent,

    AttendanceReportModalComponent,
    AttendanceDetailComponent,
    ShipmentDetailsComponent,
    CompaniesComponent,
    FactoriesComponent,
    FactoryStockComponent,
    ProductTransferComponent,
    ProductionStockComponent,
    MasterComponent,
    RolesComponent,
    DesignationsComponent,
    SalesTeamManagementComponent,
    SalesTeamRowComponent,
    ManagerPickerModalComponent,
    RemoveManagerModalComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    NgOptimizedImage,
    ReactiveFormsModule,
    FormsModule,
    ExpenseSummaryCardComponent,
    NgbTooltip,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    GoogleMapsModule,
    NgSelectModule,
    PageWrapperComponent,
    TabNavigationComponent,
    PrimaryBtnComponent,
    LoadingSpinnerComponent,
  ],
  providers: [
    provideHttpClient(withInterceptors([authInterceptor])),
    { provide: DATE_PIPE_DEFAULT_TIMEZONE, useValue: 'Asia/Kolkata' },
    { provide: LOCALE_ID, useValue: 'en-IN' },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
